import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Dimmer, Icon, Input, Loader, Modal } from 'semantic-ui-react';
import { getUsers, updateUser } from '../../../services/user';
import { UserState } from '../../../types/UserState';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../services/notification';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import { getUserInitials } from './users/utils';
import {
  CandidateAvatar,
  CandidateEmail,
  CandidateInfo,
  CandidateList,
  CandidateListItem,
  CandidateName,
  EmptyListState,
  LoadingWrapper,
  ModalDescription,
  SearchWrapper,
  SelectAllButton,
  SelectionSummary,
  SummaryBadge,
  Toolbar
} from './AssignUserModal.styles';

type AssignUserModalProps = {
  open: boolean;
  estateId: string;
  unitId: string;
  searchId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  open,
  estateId,
  unitId,
  searchId,
  onClose,
  onSuccess
}) => {
  const [candidates, setCandidates] = useState<UserState[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadCandidates = useCallback(async () => {
    if (!open || !searchId || !estateId) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await getUsers(searchId, 1, {
        estate: estateId,
        estateUnitId: 'null',
        role: UserRolesEnum.estateUser
      });
      setCandidates(response.data || []);
    } catch {
      dangerNotification('Oops...', 'Não foi possível carregar usuários disponíveis.');
      setCandidates([]);
    } finally {
      setIsLoading(false);
    }
  }, [open, searchId, estateId]);

  useEffect(() => {
    if (open) {
      setSelectedUserIds([]);
      setSearchTerm('');
      loadCandidates();
    }
  }, [open, loadCandidates]);

  const filteredCandidates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return candidates;
    }

    return candidates.filter(
      user =>
        user.name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term)
    );
  }, [candidates, searchTerm]);

  const filteredIds = useMemo(
    () => filteredCandidates.map(user => user.id),
    [filteredCandidates]
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every(id => selectedUserIds.includes(id));

  const toggleUser = (userId: string) => {
    setSelectedUserIds(current =>
      current.includes(userId)
        ? current.filter(id => id !== userId)
        : [...current, userId]
    );
  };

  const handleToggleAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedUserIds(current => current.filter(id => !filteredIds.includes(id)));
      return;
    }

    setSelectedUserIds(current =>
      Array.from(new Set([...current, ...filteredIds]))
    );
  };

  const handleAssign = async () => {
    if (selectedUserIds.length === 0) {
      dangerNotification('Oops...', 'Selecione ao menos um usuário para vincular.');
      return;
    }

    setIsSubmitting(true);

    try {
      const results = await Promise.allSettled(
        selectedUserIds.map(userId => {
          const selectedUser = candidates.find(candidate => candidate.id === userId);

          return updateUser(userId, {
            id: userId,
            estateUnitId: unitId,
            role: selectedUser?.role
          });
        })
      );

      const successCount = results.filter(result => result.status === 'fulfilled').length;
      const failureCount = results.length - successCount;

      if (successCount > 0) {
        onSuccess();
      }

      if (failureCount === 0) {
        successNotification(
          'Sucesso',
          successCount === 1
            ? 'Usuário vinculado à unidade.'
            : `${successCount} usuários vinculados à unidade.`
        );
        onClose();
        return;
      }

      if (successCount > 0) {
        warningNotification(
          'Atenção',
          `${successCount} usuário(s) vinculado(s), mas ${failureCount} falharam.`
        );
        await loadCandidates();
        setSelectedUserIds([]);
        return;
      }

      dangerNotification('Oops...', 'Não foi possível vincular os usuários selecionados.');
    } catch (error) {
      const message =
        (error as { message?: string })?.message ||
        'Não foi possível vincular os usuários à unidade.';
      dangerNotification('Oops...', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderListContent = () => {
    if (isLoading) {
      return (
        <LoadingWrapper>
          <Loader active inline="centered" />
        </LoadingWrapper>
      );
    }

    if (candidates.length === 0) {
      return (
        <EmptyListState>
          <Icon name="users" style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
          Nenhum usuário disponível para vincular.
        </EmptyListState>
      );
    }

    if (filteredCandidates.length === 0) {
      return (
        <EmptyListState>
          <Icon name="search" style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
          Nenhum usuário encontrado para &quot;{searchTerm.trim()}&quot;.
        </EmptyListState>
      );
    }

    return (
      <CandidateList role="listbox" aria-label="Usuários disponíveis para vincular" aria-multiselectable>
        {filteredCandidates.map(user => {
          const isSelected = selectedUserIds.includes(user.id);

          return (
            <CandidateListItem
              key={user.id}
              $selected={isSelected}
              role="option"
              aria-selected={isSelected}
              onClick={() => toggleUser(user.id)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  toggleUser(user.id);
                }
              }}
              tabIndex={0}
            >
              <Checkbox checked={isSelected} readOnly aria-hidden tabIndex={-1} />
              <CandidateAvatar aria-hidden>{getUserInitials(user.name || '')}</CandidateAvatar>
              <CandidateInfo>
                <CandidateName>{user.name}</CandidateName>
                <CandidateEmail>{user.email}</CandidateEmail>
              </CandidateInfo>
            </CandidateListItem>
          );
        })}
      </CandidateList>
    );
  };

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Dimmer active={isSubmitting} inverted>
        <Loader />
      </Dimmer>

      <Modal.Header>Vincular usuários</Modal.Header>
      <Modal.Content>
        <ModalDescription>
          Selecione os usuários da imobiliária que ainda não estão vinculados a uma unidade.
        </ModalDescription>

        <Toolbar>
          <SearchWrapper>
            <Input
              fluid
              icon="search"
              iconPosition="left"
              placeholder="Buscar por nome ou e-mail..."
              value={searchTerm}
              disabled={isLoading || isSubmitting || candidates.length === 0}
              onChange={(_, { value }) => setSearchTerm(String(value))}
              aria-label="Buscar usuários"
            />
          </SearchWrapper>

          <SelectionSummary>
            {selectedUserIds.length > 0 && (
              <SummaryBadge>
                {selectedUserIds.length} selecionado
                {selectedUserIds.length > 1 ? 's' : ''}
              </SummaryBadge>
            )}

            <SelectAllButton
              type="button"
              onClick={handleToggleAllFiltered}
              disabled={
                isLoading || isSubmitting || filteredCandidates.length === 0
              }
              aria-label={
                allFilteredSelected
                  ? 'Desmarcar todos os usuários visíveis'
                  : 'Marcar todos os usuários visíveis'
              }
            >
              {allFilteredSelected ? 'Desmarcar todos' : 'Marcar todos'}
              {searchTerm.trim() ? ' (filtrados)' : ''}
            </SelectAllButton>
          </SelectionSummary>
        </Toolbar>

        {renderListContent()}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          color="purple"
          onClick={handleAssign}
          loading={isSubmitting}
          disabled={isSubmitting || selectedUserIds.length === 0 || isLoading}
        >
          Vincular{selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default AssignUserModal;
